import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';

async function fetchDashboardDataFromSupabase() {
  try {
    console.log('🔄 Iniciando busca de dados do dashboard...');

    // Verificar se o usuário está autenticado
    const { data: { user }, error: authError } = await supabase.auth.getUser();

    if (authError || !user) {
      console.error('❌ Usuário não autenticado:', authError);
      throw new Error('Usuário não autenticado');
    }

    console.log('✅ Usuário autenticado:', user.email);

    // Buscar clientes (usuários) - apenas perfis básicos
    const { data: clients, error: clientsError } = await supabase
      .from('profiles')
      .select('id, full_name, email, subscription_status');

    if (clientsError) {
      console.error('Erro ao buscar clientes:', clientsError);
    } else {
      console.log('✅ Clientes encontrados:', clients?.length || 0);
    }

    // Buscar dispositivos
    const { data: devices, error: devicesError } = await supabase
      .from('devices')
      .select('id, name, status, created_at');

    if (devicesError) {
      console.error('Erro ao buscar dispositivos:', devicesError);
    }

    // Buscar mídias com informações básicas
    const { data: allMedias, error: mediasError } = await supabase
      .from('medias')
      .select('id, title, type, active, display_time, created_at, url, user_id, organization_id')
      .order('created_at', { ascending: false });

    // Buscar IDs de motoristas para excluí-los das mídias
    const { data: drivers } = await supabase
      .from('drivers')
      .select('id');

    const driverIds = new Set(drivers?.map(d => d.id) || []);

    // Filtrar mídias para excluir as de motoristas
    const medias = allMedias?.filter(m => !driverIds.has(m.user_id)) || [];

    console.log(`📊 Mídias filtradas: ${allMedias?.length || 0} total -> ${medias.length} (sem motoristas)`);

    if (mediasError) {
      console.error('Erro ao buscar mídias:', mediasError);
    } else {
      console.log('✅ Mídias encontradas:', medias?.length || 0);
      console.log('📊 Primeiras mídias:', medias?.slice(0, 3));
    }

    // Buscar perfis dos usuários com emails usando função RPC (mesmo método da seção Clientes & Financeiro)
    console.log('🔄 Tentando usar função RPC get_profiles_with_email...');

    let profilesWithEmails: any[] = [];
    let profilesError: any = null;

    try {
      const result = await (supabase as any).rpc('get_profiles_with_email');
      profilesWithEmails = result.data || [];
      profilesError = result.error;
      console.log('✅ Função RPC executada com sucesso:', profilesWithEmails.length, 'registros');
    } catch (error) {
      console.error('❌ Erro ao chamar função RPC:', error);
      profilesError = error;
    }

    // Se a função RPC falhar, usar fallback
    if (profilesError || !profilesWithEmails || profilesWithEmails.length === 0) {
      console.log('⚠️ Função RPC falhou, usando fallback...');

      // Buscar perfis diretamente
      const { data: basicProfiles, error: basicError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .order('created_at', { ascending: false });

      if (basicError) {
        console.error('❌ Erro ao buscar perfis básicos:', basicError);
        profilesWithEmails = [];
      } else {
        console.log('✅ Perfis básicos encontrados:', basicProfiles?.length || 0);

        // Mapear emails baseado no ID conhecido (mesmo método da seção Clientes & Financeiro)
        profilesWithEmails = basicProfiles?.map(profile => {
          let email = null;
          if (profile.id === 'b0d46c0e-f00c-48bf-a99d-283c4f992b33') {
            email = 'desenvolvimento@locaninja.com';
          } else if (profile.id === 'd4cdcd56-8199-4969-b2d1-de1885a4864f') {
            email = 'malandro@gmail.com';
          }

          return {
            ...profile,
            email: email || 'N/A'
          };
        }) || [];
      }
    }

    console.log(`✅ Encontrados ${profilesWithEmails?.length || 0} perfis com emails`);
    console.log('📊 Primeiros perfis:', profilesWithEmails?.slice(0, 3));

    // Usar profilesWithEmails como profiles
    const profiles = profilesWithEmails || [];

    // Buscar telas
    const { data: screens, error: screensError } = await supabase
      .from('screens')
      .select('id, name, created_at');

    if (screensError) {
      console.error('Erro ao buscar telas:', screensError);
    }

    // Criar mapa de usuários para relacionamento
    const userMap = clients?.reduce((acc: any, profile: any) => {
      acc[profile.id] = profile;
      return acc;
    }, {}) || {};

    // Criar mapa de perfis para relacionamento com mídias
    const profilesMap = profiles.reduce((acc: any, profile: any) => {
      acc[profile.id] = profile;
      return acc;
    }, {});

    // Análise de mídias por cliente (dados reais) - excluindo motoristas e admins
    const mediaAnalytics = medias?.reduce((acc: any, media: any) => {
      const profile = profilesMap[media.user_id];

      // Excluir motoristas
      if (driverIds.has(media.user_id)) return acc;

      // Excluir admins
      if (profile?.email?.toLowerCase().includes('admin')) return acc;

      const clientName = profile?.full_name || 'Cliente Sem Nome';
      const clientEmail = profile?.email || 'N/A';

      console.log(`🔍 Mídia ${media.title} - user_id: ${media.user_id}, profile:`, profile);

      if (!acc[clientName]) {
        acc[clientName] = {
          name: clientName,
          email: clientEmail,
          userId: media.user_id,
          totalMedias: 0,
          activeMedias: 0,
          totalDisplayTime: 0,
          medias: [],
          lastCreated: null,
          avgDisplayTime: 0,
          activeDevices: 0
        };
      }

      acc[clientName].totalMedias++;
      acc[clientName].medias.push({
        id: media.id,
        title: media.title,
        type: media.type,
        created_at: media.created_at,
        active: media.active,
        display_time: media.display_time || 0
      });

      if (media.active) {
        acc[clientName].activeMedias++;
      }

      acc[clientName].totalDisplayTime += media.display_time || 0;

      const mediaDate = new Date(media.created_at);
      if (!acc[clientName].lastCreated || mediaDate > new Date(acc[clientName].lastCreated)) {
        acc[clientName].lastCreated = media.created_at;
      }

      return acc;
    }, {}) || {};

    // Calcular média de tempo de exibição por cliente e buscar telas/dispositivos ativos
    await Promise.all(Object.values(mediaAnalytics).map(async (client: any) => {
      client.avgDisplayTime = client.totalMedias > 0 ? Math.round(client.totalDisplayTime / client.totalMedias) : 0;

      // Buscar dispositivos ativos do cliente
      if (client.userId) {
        try {
          const { data: devices } = await supabase
            .from('devices')
            .select('id, name, status')
            .eq('user_id', client.userId);

          client.activeDevices = devices?.filter(device => device.status === 'active').length || 0;
          console.log(`📱 Cliente ${client.name} - Aparelhos ativos:`, client.activeDevices);
        } catch (error) {
          console.error(`❌ Erro ao buscar aparelhos do cliente ${client.name}:`, error);
          client.activeDevices = 0;
        }
      }
    }));

    // Top mídias mais longas (excluindo motoristas e admins)
    const topLongestMedias = medias
      ?.filter(media => {
        // Excluir motoristas e admins
        if (driverIds.has(media.user_id)) return false;
        const profile = profilesMap[media.user_id];
        if (profile?.email?.toLowerCase().includes('admin')) return false;
        return media.display_time && media.display_time > 0;
      })
      .sort((a, b) => (b.display_time || 0) - (a.display_time || 0))
      .slice(0, 10)
      .map(media => {
        const profile = profilesMap[media.user_id];
        console.log(`🔍 Top mídia ${media.title} - user_id: ${media.user_id}, profile:`, profile);
        return {
          id: media.id,
          title: media.title,
          display_time: media.display_time,
          client: profile?.full_name || 'Cliente Sem Nome',
          type: media.type,
          created_at: media.created_at,
          url: media.url
        };
      }) || [];

    // Mídias criadas por período (últimos 30 dias) - excluindo motoristas e admins
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentMedias = medias?.filter(media => {
      // Excluir motoristas e admins
      if (driverIds.has(media.user_id)) return false;
      const profile = profilesMap[media.user_id];
      if (profile?.email?.toLowerCase().includes('admin')) return false;
      return new Date(media.created_at) >= thirtyDaysAgo;
    }).map(media => {
      const profile = profilesMap[media.user_id];
      console.log(`🔍 Mídia recente ${media.title} - user_id: ${media.user_id}, profile:`, profile);
      return {
        ...media,
        client: profile?.full_name || 'Cliente Sem Nome'
      };
    }) || [];

    // Estatísticas gerais de mídias
    const mediaStats = {
      total: medias?.length || 0,
      active: medias?.filter(m => m.active).length || 0,
      inactive: medias?.filter(m => !m.active).length || 0,
      images: medias?.filter(m => m.type === 'image').length || 0,
      videos: medias?.filter(m => m.type === 'video').length || 0,
      recent: recentMedias.length,
      avgDisplayTime: medias?.length > 0
        ? Math.round(medias.reduce((sum, m) => sum + (m.display_time || 0), 0) / medias.length)
        : 0
    };

    // KPIs - Excluir motoristas, admins e filtrar apenas clientes ativos (donos de estabelecimentos)
    const validClients = clients?.filter(client => {
      // Excluir admins (qualquer email contendo 'admin')
      if (client.email?.toLowerCase().includes('admin')) return false;
      // Excluir motoristas
      if (driverIds.has(client.id)) return false;
      // Apenas clientes com assinatura ativa
      if (client.subscription_status !== 'active') return false;
      return true;
    }) || [];
    const activeClients = validClients.length;

    console.log(`👥 Clientes Ativos (KPI): ${activeClients} (excluídos admins e ${driverIds.size} motoristas)`);

    // Calcular MRR real somando plan_price de todos os clientes ativos (excluindo motoristas e admins)
    // Buscar plan_price diretamente dos profiles
    let totalMRR = 0;
    try {
      const { data: activeProfiles } = await supabase
        .from('profiles')
        .select('id, plan_price, subscription_status, email')
        .eq('subscription_status', 'active');

      if (activeProfiles) {
        // Filtrar para excluir motoristas e admins
        const filteredProfiles = activeProfiles.filter(p =>
          !driverIds.has(p.id) &&
          !p.email?.toLowerCase().includes('admin')
        );

        totalMRR = filteredProfiles.reduce((sum, profile) => {
          const price = typeof profile.plan_price === 'string'
            ? parseFloat(profile.plan_price)
            : Number(profile.plan_price || 0);
          return sum + (isNaN(price) ? 0 : price);
        }, 0);

        console.log('💰 MRR calculado:', totalMRR, 'de', filteredProfiles.length, 'clientes ativos (sem motoristas/admins)');
      }
    } catch (error) {
      console.error('❌ Erro ao calcular MRR:', error);
    }

    // Formatar MRR para exibição
    const formattedMRR = `R$ ${totalMRR.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // Novo cálculo de telas online/offline baseado em dispositivos
    let onlineDevices = 0;
    let offlineDevices = 0;
    if (devices) {
      onlineDevices = devices.filter(d => d.status === 'active').length;
      offlineDevices = devices.length - onlineDevices;
    }

    // Gerar dados reais dos gráficos baseados nos clientes existentes
    // Agrupar clientes por mês de criação (últimos 6 meses)
    const now = new Date();
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const revenueChartData: { month: string; mrr: number; arr: number; churn: number }[] = [];
    const customerChartData: { month: string; newClients: number; cac: number }[] = [];

    // Buscar todos os perfis com datas para calcular gráficos
    const { data: allProfilesForChart } = await supabase
      .from('profiles')
      .select('id, created_at, subscription_status, plan_price')
      .neq('email', 'master@admin.com')
      .order('created_at', { ascending: true });

    // Calcular dados dos últimos 6 meses
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      const monthName = monthNames[monthDate.getMonth()];

      // Clientes criados neste mês
      const newClientsThisMonth = allProfilesForChart?.filter(p => {
        const createdAt = new Date(p.created_at);
        return createdAt >= monthDate && createdAt <= monthEnd;
      }).length || 0;

      // MRR acumulado até o final deste mês (clientes ativos até esta data)
      const activeClientsUpToMonth = allProfilesForChart?.filter(p => {
        const createdAt = new Date(p.created_at);
        return createdAt <= monthEnd && p.subscription_status === 'active';
      }) || [];

      const monthlyMRR = activeClientsUpToMonth.reduce((sum, p) => {
        const price = typeof p.plan_price === 'string'
          ? parseFloat(p.plan_price)
          : Number(p.plan_price || 0);
        return sum + (isNaN(price) ? 0 : price);
      }, 0);

      revenueChartData.push({
        month: monthName,
        mrr: Math.round(monthlyMRR),
        arr: Math.round(monthlyMRR * 12),
        churn: 0 // Churn seria calculado com base em cancelamentos, simplificado por agora
      });

      customerChartData.push({
        month: monthName,
        newClients: newClientsThisMonth,
        cac: 0 // CAC não está disponível, zerado por agora
      });
    }

    console.log('📊 Dados dos gráficos calculados:', { revenueChartData, customerChartData });

    // Calcular KPIs adicionais
    const totalARR = totalMRR * 12;
    const formattedARR = `R$ ${totalARR.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const ticketMedio = activeClients > 0 ? totalMRR / activeClients : 0;
    const formattedTicketMedio = `R$ ${ticketMedio.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // Buscar dados para gráficos de pizza
    // Distribuição de status de clientes
    const clientStatusData = [
      { name: 'Ativos', value: activeClients, color: '#10B981' },
      { name: 'Inativos', value: (clients?.length || 0) - activeClients, color: '#EF4444' },
    ];

    // Distribuição de dispositivos
    const deviceStatusData = [
      { name: 'Online', value: onlineDevices, color: '#10B981' },
      { name: 'Offline', value: offlineDevices, color: '#6B7280' },
    ];

    // Distribuição de mídias por tipo
    // Distribuição de mídias por tipo
    const typeCounts = {
      image: 0,
      gif: 0,
      video: 0,
      youtube: 0,
      others: 0
    };

    console.log('🔍 Iniciando contagem de tipos de mídia. Total mídias:', medias?.length);

    medias?.forEach(m => {
      const url = (m.url || '').toLowerCase();
      const type = (m.type || '').toLowerCase();

      console.log(`Media ID: ${m.id}, Type: ${m.type}, URL: ${m.url}`);

      // YouTube detection
      if (type === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
        typeCounts.youtube++;
        return;
      }

      // GIF detection
      if (type === 'gif' || url.endsWith('.gif')) {
        typeCounts.gif++;
        return;
      }

      // Video detection
      if (type === 'video' || url.endsWith('.mp4') || url.endsWith('.webm')) {
        typeCounts.video++;
        return;
      }

      // Image detection
      if (type === 'image' || url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.png') || url.endsWith('.webp')) {
        typeCounts.image++;
        return;
      }

      // Se chegou aqui, é outro tipo
      typeCounts.others++;
      console.log('⚠️ Tipo não identificado classificado como Outros:', m);
    });

    console.log('📊 Contagem final de tipos:', typeCounts);

    const mediaTypeData = [
      { name: 'Imagens', value: typeCounts.image, color: '#3B82F6' },
      { name: 'GIFs', value: typeCounts.gif, color: '#EC4899' },
      { name: 'Vídeos', value: typeCounts.video, color: '#8B5CF6' },
      { name: 'YouTube', value: typeCounts.youtube, color: '#FF0000' },
      { name: 'Outros', value: typeCounts.others, color: '#9CA3AF' },
    ];

    // Buscar distribuição de planos
    const { data: planDistribution } = await supabase
      .from('profiles')
      .select('subscription_plan')
      .neq('email', 'master@admin.com')
      .eq('subscription_status', 'active');

    // Agrupar por plano
    const planCounts: Record<string, number> = {};
    planDistribution?.forEach(profile => {
      const plan = profile.subscription_plan || 'Sem Plano';
      planCounts[plan] = (planCounts[plan] || 0) + 1;
    });

    const planColors = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4'];
    const planDistributionData = Object.entries(planCounts).map(([name, value], index) => ({
      name,
      value,
      color: planColors[index % planColors.length]
    }));

    // Preparar lista completa de mídias com informações do cliente
    const allMediasWithClients = medias?.map(media => {
      const profile = profilesMap[media.user_id];
      return {
        id: media.id,
        title: media.title,
        type: media.type,
        url: media.url,
        active: media.active,
        display_time: media.display_time,
        created_at: media.created_at,
        clientName: profile?.full_name || 'Cliente Sem Nome',
        clientEmail: profile?.email || 'N/A',
      };
    }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) || [];

    console.log('📊 allMediasWithClients gerado:', allMediasWithClients.length, 'mídias');
    console.log('📊 Primeiras 3 mídias:', allMediasWithClients.slice(0, 3));

    // Montar objeto para o dashboard
    const dashboardData = {
      kpis: [
        { title: 'MRR', value: formattedMRR, icon: 'trending-up', status: 'positive' as const },
        { title: 'ARR (Projetado)', value: formattedARR, icon: 'calendar', status: 'positive' as const },
        { title: 'Ticket Médio', value: formattedTicketMedio, icon: 'receipt', status: 'neutral' as const },
        { title: 'Clientes Ativos', value: activeClients, icon: 'users', status: 'positive' as const },
        { title: 'Dispositivos Online', value: `${onlineDevices}/${onlineDevices + offlineDevices}`, icon: 'monitor', status: 'neutral' as const },
        { title: 'Total de Mídias', value: mediaStats.total, icon: 'file-video', status: 'positive' as const },
        { title: 'Mídias Ativas', value: mediaStats.active, icon: 'play', status: 'positive' as const },
        { title: 'Telas Criadas', value: screens?.length || 0, icon: 'layout', status: 'neutral' as const },
      ],
      revenueChart: revenueChartData,
      customerChart: customerChartData,
      // Dados para gráficos de pizza
      pieCharts: {
        clientStatus: clientStatusData,
        deviceStatus: deviceStatusData,
        mediaType: mediaTypeData,
        planDistribution: planDistributionData,
      },
      mediaAnalytics: Object.values(mediaAnalytics),
      mediaStats,
      topLongestMedias,
      recentMedias,
      allMediasWithClients,
      screens: screens || []
    };

    console.log('📊 Dados do dashboard montados:', {
      mediaAnalytics: dashboardData.mediaAnalytics.length,
      topLongestMedias: dashboardData.topLongestMedias.length,
      recentMedias: dashboardData.recentMedias.length,
      mediaStats: dashboardData.mediaStats
    });

    return dashboardData;
  } catch (error) {
    console.error('Erro geral no fetchDashboardDataFromSupabase:', error);
    // Retornar dados básicos em caso de erro
    return {
      kpis: [
        { title: 'MRR', value: 'R$ 0,00', icon: 'trending-up', status: 'neutral' as const },
        { title: 'Clientes Ativos', value: 0, icon: 'users', status: 'neutral' as const },
        { title: 'Dispositivos Online', value: '0/0', icon: 'monitor', status: 'neutral' as const },
        { title: 'Total de Mídias', value: 0, icon: 'file-video', status: 'neutral' as const },
      ],
      revenueChart: [],
      customerChart: [],
      mediaAnalytics: [],
      mediaStats: {
        total: 0,
        active: 0,
        inactive: 0,
        images: 0,
        videos: 0,
        recent: 0,
        avgDisplayTime: 0
      },
      topLongestMedias: [],
      recentMedias: [],
      allMediasWithClients: [],
      screens: []
    };
  }
}

export const useDashboardData = () => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: fetchDashboardDataFromSupabase,
    refetchInterval: 300000,
    staleTime: 60000,
  });

  return { dashboardData: data, isLoading, error, refresh: refetch };
};